import { ArrowDropDown, ArrowDropUp, Close, FilterList, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  TextField
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC, useState, useEffect, Dispatch, SetStateAction } from 'react';
import {
  getPlaceOrderDescriptions,
  getPlaceOrderOwners,
  getPlaceOrderStatuses,
  getPlaceOrderTypes
} from '../../../fetch';
import {
  IVendor,
  INeighborhood,
  IOrderOwner,
  IOrderStatus,
  IOrderType,
  IDateRange,
  ILot
} from '../../../models';
import { DateRangePicker, SMBSelect } from '../../../components';
import useCollapse from 'react-collapsed';
import { getCommonNeighborhoodLots, getProductionNeighborhoods, getProductionVendors } from '../../../fetch/lookups';

export interface IFilters {
  selectedOrderType: string;
  selectedNeighborhoodId: string;
  selectedStatus: string;
  selectedDescription: string;
  selectedOwner: string;
  selectedLotId: string;
  selectedVendorId: string;
}

interface IOrderManagementFilters {
  isLoading: boolean;
  applyFilters: (data?: IFilters, clearFilters?: boolean) => void;
  setSearchValue: (val: string) => void;
  searchValue: string;
  handleSearch: (data?: IFilters, clearSearch?: boolean) => void;
  selectedVendorId: string;
  setSelectedVendorId: (val: any) => void;
  selectedOwner: string;
  setSelectedOwner: (val: any) => void;
  selectedOrderType: string;
  setSelectedOrderType: (val: any) => void;
  selectedDescription: string;
  setSelectedDescription: (val: any) => void;
  selectedLotId: string;
  setSelectedLotId: (val: any) => void;
  selectedNeighborhoodId: string;
  setSelectedNeighborhoodId: (val: any) => void;
  selectedStatus:
    | 'New'
    | 'Approved'
    | 'Remediating'
    | 'Placed'
    | 'Delivered'
    | 'Cancelled'
    | string;
  setSelectedStatus: (val: any) => void;
  hasAppliedFiltersGlobal?: boolean;
  setParentOrderTypes: (val: IOrderType[]) => void;
  selectedDateRange: IDateRange | null;
  setSelectedDateRange: Dispatch<SetStateAction<IDateRange>>;
}

export const OrderManagementFilters: FC<IOrderManagementFilters> = ({
  isLoading,
  applyFilters,
  setParentOrderTypes,
  setSearchValue,
  handleSearch,
  searchValue,
  selectedVendorId,
  setSelectedVendorId,
  selectedOwner,
  setSelectedOwner,
  selectedOrderType,
  setSelectedOrderType,
  selectedDescription,
  setSelectedDescription,
  selectedLotId,
  setSelectedLotId,
  selectedNeighborhoodId,
  setSelectedNeighborhoodId,
  selectedStatus,
  setSelectedStatus,
  hasAppliedFiltersGlobal,
  selectedDateRange,
  setSelectedDateRange
}) => {
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(hasAppliedFiltersGlobal);
  const [isLoadingOwners, setLoadingOwners] = useState<boolean>(false);
  const [isLoadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  const [vendors, setVendors] = useState<IVendor[] | null>(null);
  const [isLoadingVendors, setLoadingVendors] = useState(false);
  const [vendorValue, setVendorValue] = useState<IVendor | null>(null);
  const [loadingDescriptions, setLoadingDescriptions] = useState(false);
  const [descriptions, setDescriptions] = useState(null);
  const [isLoadingNeighborhoods, setNeighborhoodsLoading] = useState<boolean>(true);
  const [isLoadingNeighborhoodLots, setNeighborhoodsLotsLoading] = useState<boolean>(false);
  const [orderTypes, setOrderTypes] = useState<IOrderType[] | null>(null);
  const [owners, setOwners] = useState<IOrderOwner[] | null>(null);
  const [statuses, setStatuses] = useState<IOrderStatus[] | null>(null);
  const [isLoadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>(null);
  const [lots, setLots] = useState<ILot[]>(null);
  const { getCollapseProps, getToggleProps, isExpanded, setExpanded } = useCollapse();
  const fetchOrderOwners = async () => {
    setLoadingOwners(true);
    try {
      const res = await getPlaceOrderOwners();
      setOwners(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingOwners(false);
  };

  const fetchOrderStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const res = await getPlaceOrderStatuses();
      setStatuses(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingStatuses(false);
  };

  const fetchOrderTypes = async () => {
    setLoadingTypes(true);
    try {
      const res = await getPlaceOrderTypes({
        isActive: true
      });
      setOrderTypes(res);
      setParentOrderTypes(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingTypes(false);
  };
  const fetchDescriptionsTypes = async () => {
    setLoadingDescriptions(true);
    try {
      const res = await getPlaceOrderDescriptions({
        isActive: true
      });
      setDescriptions(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingDescriptions(false);
  };

  const fetchNeighborhoods = async () => {
    try {
      const res = await getProductionNeighborhoods({ hasActiveJobs: false });
      setNeighborhoods(res.records);
    } catch (error) {
      console.error(error);
    }
    setNeighborhoodsLoading(false);
  };

  const fetchNeighborhoodLots = async () => {
    setNeighborhoodsLotsLoading(true);
    try {
      const res = await getCommonNeighborhoodLots({
        neighborhoodId: Number(selectedNeighborhoodId),
        includeGeneralJob: true
      });
      setLots(res);
    } catch (error) {
      console.error(error);
    }
    setNeighborhoodsLotsLoading(false);
  };
  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const res = await getProductionVendors();
      setVendors(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingVendors(false);
    }
  };
  const classes = useStyles();

  useEffect(() => {
    fetchNeighborhoods();
    fetchOrderStatuses();
    fetchOrderTypes();
    fetchDescriptionsTypes();
    fetchOrderOwners();
    fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedNeighborhoodId) {
      fetchNeighborhoodLots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhoodId]);

  useEffect(() => {
    if (hasAppliedFiltersGlobal) {
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedVendorId && hasAppliedFiltersGlobal) {
      setVendorValue(vendors?.find(v => v.sageId === +selectedVendorId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendorId, vendors]);

  return (
    <>
      <Box
        display="flex"
        marginTop="1rem"
        paddingBottom="1rem"
        width={{
          xs: '100%',
          sm: 'auto'
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          className={classes.mobileButton}
          startIcon={<FilterList />}
          endIcon={isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
          {...getToggleProps()}
        >
          Filters
        </Button>
      </Box>
      {!isExpanded && <Divider className={classes.divider} />}
      <Box {...getCollapseProps()}>
        <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search..."
              name="search"
              value={searchValue}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={classes.searchIcon}
                    onClick={() => {
                      if (searchValue.length > 0) {
                        setHasAppliedFilters(true);
                        handleSearch({
                          selectedOrderType,
                          selectedNeighborhoodId,
                          selectedStatus,
                          selectedDescription,
                          selectedOwner,
                          selectedLotId,
                          selectedVendorId
                        });
                      }
                    }}
                  >
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment
                    position="end"
                    className={classes.searchIcon}
                    onClick={() => {
                      setSearchValue('');
                      handleSearch(undefined, true);
                    }}
                  >
                    <Close />
                  </InputAdornment>
                ) : null
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && searchValue.length > 0) {
                  setHasAppliedFilters(true);
                  handleSearch({
                    selectedOrderType,
                    selectedNeighborhoodId,
                    selectedStatus,
                    selectedDescription,
                    selectedOwner,
                    selectedLotId,
                    selectedVendorId
                  });
                }
              }}
              onChange={e => {
                setSearchValue(e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <Autocomplete
                value={vendorValue}
                classes={{
                  paper: classes.paperBorder
                }}
                onChange={(event, newValue: IVendor) => {
                  setVendorValue(newValue);
                  if (newValue) {
                    setSelectedVendorId(`${newValue.sageId}`);
                  } else {
                    setSelectedVendorId('');
                  }
                }}
                id="vendors"
                options={vendors || []}
                disabled={isLoadingVendors}
                getOptionLabel={(option: IVendor) => {
                  // Regular option
                  return option.name;
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.vendorId}>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    autoComplete="none"
                    label="Vendors"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SMBSelect
              variant="outlined"
              handleClear={() => setSelectedOwner('')}
              disabled={isLoadingOwners}
              name="owner"
              label="Owner"
              id="owner"
              value={selectedOwner}
              onChange={({ target: { value } }) => setSelectedOwner(value)}
            >
              {owners &&
                owners.map((owner, index) => {
                  return (
                    <MenuItem key={`${index}`} value={owner.name}>
                      {owner.name}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SMBSelect
              variant="outlined"
              disabled={isLoading || isLoadingTypes}
              name="type"
              label="Order Type"
              id="type"
              value={selectedOrderType}
              onChange={({ target: { value } }) => setSelectedOrderType(value)}
              handleClear={() => setSelectedOrderType('')}
            >
              {orderTypes &&
                orderTypes.map((orderType, index) => {
                  return (
                    <MenuItem key={`${index}`} value={orderType.sageId}>
                      {orderType.typeName}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SMBSelect
              variant="outlined"
              disabled={isLoading || loadingDescriptions}
              name="description"
              label="Order Desc."
              id="description"
              value={selectedDescription}
              onChange={({ target: { value } }) => setSelectedDescription(value)}
              handleClear={() => setSelectedDescription('')}
            >
              {descriptions &&
                descriptions.map((description, index) => {
                  return (
                    <MenuItem key={`${index}`} value={description.id}>
                      {description.name}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SMBSelect
              variant="outlined"
              disabled={isLoading || isLoadingNeighborhoods}
              name="neighborhood"
              label="Neighborhood"
              id="neighborhood"
              value={selectedNeighborhoodId}
              onChange={({ target: { value } }) => setSelectedNeighborhoodId(value)}
              handleClear={() => setSelectedNeighborhoodId('')}
            >
              {neighborhoods &&
                neighborhoods.map((neighborhood, index) => {
                  return (
                    <MenuItem key={`${index}`} value={neighborhood.neighborhoodId}>
                      {neighborhood.name} Phase {neighborhood.phase}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          {selectedNeighborhoodId && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SMBSelect
                variant="outlined"
                disabled={isLoading || isLoadingNeighborhoodLots}
                name="lots"
                label="Lots"
                id="lots"
                value={selectedLotId}
                onChange={({ target: { value } }) => setSelectedLotId(value)}
                handleClear={() => setSelectedLotId('')}
              >
                {lots &&
                  lots.map((lot, index) => {
                    return (
                      <MenuItem key={`${index}`} value={lot.lotNumber}>
                        {lot.description}
                      </MenuItem>
                    );
                  })}
              </SMBSelect>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SMBSelect
              variant="outlined"
              disabled={isLoadingStatuses}
              name="status"
              label="Status"
              id="status"
              value={selectedStatus}
              onChange={({ target: { value } }) => setSelectedStatus(value)}
              handleClear={() => setSelectedStatus('')}
            >
              {statuses &&
                statuses.map((status, index) => {
                  return (
                    <MenuItem key={`${index}`} value={status.value}>
                      {status.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <DateRangePicker
                id="filterDateRange"
                label="Date Range"
                value={selectedDateRange}
                isDisabled={isLoading}
                onChange={value => {
                  setSelectedDateRange(value);
                }}
                fullWidth
                inputSize="small"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} pb={1}>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              disabled={isLoading}
              startIcon={<FilterList />}
              onClick={() => {
                setHasAppliedFilters(true);
                applyFilters({
                  selectedOrderType,
                  selectedNeighborhoodId,
                  selectedStatus,
                  selectedDescription,
                  selectedOwner,
                  selectedLotId,
                  selectedVendorId
                });
              }}
            >
              Apply Filters
            </Button>
            {hasAppliedFilters && (
              <Button
                color="inherit"
                className={clsx(classes.button, classes.resetButton)}
                variant="contained"
                disabled={isLoading}
                startIcon={<Close />}
                onClick={() => {
                  setHasAppliedFilters(false);
                  setSearchValue('');
                  setSelectedNeighborhoodId('');
                  setSelectedLotId('');
                  setSelectedOrderType('');
                  setSelectedOwner('');
                  setSelectedStatus('');
                  setSelectedDescription('');
                  setSelectedVendorId('');
                  setVendorValue(null);
                  setSelectedDateRange(null);
                  applyFilters(undefined, true);
                }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  button: {
    marginTop: 8,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
